import { RefObject } from 'react';
import {
  GridApiPro,
  GridColDef,
  GridValidRowModel,
  gridColumnFieldsSelector,
} from '@mui/x-data-grid-pro';

export const CELL_HORIZONTAL_PADDING = 32;
const MIN_CELL_TEXT_WIDTH = 32;

export const getCellTextWidth = (
  computedWidth?: number,
): string | undefined => {
  if (!computedWidth || Number.isNaN(computedWidth)) return undefined;
  const adjusted = Math.max(
    computedWidth - CELL_HORIZONTAL_PADDING,
    MIN_CELL_TEXT_WIDTH,
  );
  return `${adjusted}px`;
};

type PersistedColumnLike = {
  field: string;
  width?: number | null;
  flex?: number | null;
};

export const buildGridColumns = <
  TRow extends GridValidRowModel,
  TPersisted extends PersistedColumnLike,
>(
  runtimeColumns: GridColDef<TRow>[],
  persistedColumns: TPersisted[],
): GridColDef<TRow>[] => {
  const runtimeByField = new Map(runtimeColumns.map(col => [col.field, col]));
  const merged: GridColDef<TRow>[] = [];

  for (const persisted of persistedColumns) {
    const runtime = runtimeByField.get(persisted.field);
    if (!runtime) continue;

    const { flex: _runtimeFlex, ...runtimeRest } = runtime;
    const hasPersistedWidth = persisted.width != null && persisted.flex == null;

    merged.push({
      ...(hasPersistedWidth ? runtimeRest : runtime),
      ...persisted,
      ...(hasPersistedWidth ? { flex: undefined } : {}),
      // Runtime columns are the source of truth for behaviour flags —
      // never let a stale persisted value override them.
      sortable: runtime.sortable,
      filterable: runtime.filterable,
      disableColumnMenu: runtime.disableColumnMenu,
    } as GridColDef<TRow>);

    runtimeByField.delete(persisted.field);
  }

  for (const remaining of runtimeByField.values()) {
    merged.push(remaining);
  }

  return merged;
};

export const getReorderedColumns = <TRow extends GridValidRowModel>(
  apiRef: RefObject<GridApiPro | null>,
  columns: GridColDef<TRow>[],
): GridColDef<TRow>[] => {
  if (!apiRef.current) return columns;

  const orderedFields = gridColumnFieldsSelector(apiRef);
  const byField = new Map(columns.map(col => [col.field, col]));

  return orderedFields
    .map(field => byField.get(field))
    .filter((col): col is GridColDef<TRow> => Boolean(col));
};
